import React, {
  FC,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams } from 'react-router-dom';
import { Modal, Spinner } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import {
  GetGoogleLinkedAccountsResponse,
  GetMondayLinkedAccountsResponse,
  GetAgentWhatsappNumberResponse,
  GoogleLinkedAccount,
} from '@dreamai/types';
import { ReactComponent as GoogleLogo } from '../../icons/google-logo.svg';
import { ReactComponent as WhatsappIcon } from '../../icons/whatsapp.svg';
import { ReactComponent as LinkedinIcon } from '../../icons/linkedin-blue.svg';
import { ReactComponent as TodoistLogo } from '../../icons/todoist-logo.svg';
import { ReactComponent as AsanaLogo } from '../../icons/asana-logo.svg';
import xeroLogo from '../../icons/xero.png';
import microsoftLogo from '../../icons/microsoft.png';
import outlookLogo from '../../icons/outlook.png';
import granolaLogo from '../../icons/granola.png';
import lovableLogo from '../../icons/lovable.png';
import { ReactComponent as SlackLogo } from '../../icons/slack-logo.svg';
import { ReactComponent as LinearLogo } from '../../icons/linear-logo.svg';
import { ReactComponent as DevinLogo } from '../../icons/devin-logo.svg';
import { ReactComponent as AttioLogo } from '../../icons/attio-logo.svg';
import { ReactComponent as HubspotLogo } from '../../icons/hubspot-logo.svg';
import { ReactComponent as MondayLogo } from '../../icons/monday-logo.svg';
import { ReactComponent as NotionLogo } from '../../icons/notion-logo.svg';
import { ReactComponent as LushaLogo } from '../../icons/lusha-logo.svg';
import { ReactComponent as ZoomLogo } from '../../icons/zoom-logo.svg';
import { useMessaging } from '../../contexts/MessagingContext';
import { useAgent } from '../../contexts/AgentContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import api from '../../api';
import IntegrationPanel from './IntegrationPanel';
import DisconnectAppModal from './DisconnectAppModal';
import {
  disconnectPath,
  type DisconnectTarget,
} from './connectedAppDisconnect';
import ConnectedAppActivityView from './ConnectedAppActivityView';
import {
  OcadoAppView,
  OcadoLogo,
  useOcadoConnection,
} from './OcadoConnectedApp';
import {
  useWhatsappMacDevices,
  WhatsappMacSection,
} from './WhatsappMacConnectedApp';
import {
  LinkedinConnectedApp,
  useLinkedinConnection,
} from './LinkedinConnectedApp';
import CircleMinusIcon from './CircleMinusIcon';
import { GetConnectedAppActivityResponse } from './connectedAppActivityTypes';
import { MONDAY_PROVIDER } from './integrationProvider';
import './connected-accounts-section.scss';

type ConnectedAppsView =
  | 'list'
  | 'email'
  | 'outlook'
  | 'whatsapp'
  | 'linkedin'
  | 'todoist'
  | 'asana'
  | 'xero'
  | 'granola'
  | 'lovable'
  | 'slack'
  | 'linear'
  | 'monday'
  | 'notion'
  | 'zoom'
  | 'attio'
  | 'hubspot'
  | 'lusha'
  | 'devin'
  | 'ocado'
  | 'activity';

const NO_MAILBOX_MESSAGE =
  'Google reports that Gmail is not enabled for this account (its Workspace no longer includes Gmail, or it is a Google login for a non-Gmail address), so your secretary cannot read, search or draft email from it. Calendar, contacts and documents still work. If your email lives somewhere else, connect that account too.';

type TeamsMeetingLinkStatus = 'enabled' | 'available' | 'unsupported';

interface MicrosoftLinkedAccount {
  id: number;
  email: string;
  scope: string;
  missingCapabilities?: string[];
  teamsMeetingLinks?: TeamsMeetingLinkStatus;
}

const CONNECTED_APP_VIEWS: readonly ConnectedAppsView[] = [
  'email',
  'outlook',
  'whatsapp',
  'todoist',
  'asana',
  'xero',
  'granola',
  'lovable',
  'slack',
  'linear',
  'monday',
  'notion',
  'zoom',
  'attio',
  'hubspot',
  'lusha',
  'devin',
  'ocado',
  'linkedin',
  'activity',
];

const viewForAppParam = (app: string | null): ConnectedAppsView | undefined => {
  const normalized = app?.toLowerCase();
  if (normalized === 'whatsapp_mac') return 'whatsapp';
  return normalized &&
    CONNECTED_APP_VIEWS.includes(normalized as ConnectedAppsView)
    ? (normalized as ConnectedAppsView)
    : undefined;
};

const ChevronRightIcon: FC = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
    <path
      d='M9 6L15 12L9 18'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ArrowRightIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M4.16699 10H15.8337M15.8337 10L10.0003 4.16667M15.8337 10L10.0003 15.8333'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ActivityIcon: FC = () => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
    <path
      d='M18.3333 10H15L12.5 17.5L7.5 2.5L5 10H1.66667'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const LinkIcon: FC = () => (
  <svg width='32' height='32' viewBox='0 0 32 32' fill='none'>
    <path
      d='M13.3333 18.6667C13.9059 19.4321 14.6363 20.0654 15.4751 20.5236C16.3139 20.9818 17.2415 21.2542 18.1947 21.3224C19.1479 21.3906 20.1045 21.2528 21.0001 20.9184C21.8956 20.584 22.7089 20.0607 23.3847 19.384L27.3847 15.384C28.5988 14.127 29.2706 12.4434 29.2554 10.6957C29.2402 8.94799 28.5392 7.27633 27.3035 6.04058C26.0677 4.80484 24.3961 4.10385 22.6484 4.08867C20.9007 4.07348 19.2171 4.74528 17.96 5.95935L15.6667 8.23935M18.6667 13.3333C18.0941 12.5679 17.3637 11.9346 16.5249 11.4764C15.6861 11.0182 14.7585 10.7458 13.8053 10.6776C12.8521 10.6094 11.8955 10.7472 10.9999 11.0816C10.1044 11.416 9.29113 11.9393 8.61533 12.616L4.61533 16.616C3.40126 17.873 2.72946 19.5566 2.74465 21.3043C2.75983 23.052 3.46082 24.7237 4.69657 25.9594C5.93231 27.1952 7.60397 27.8961 9.35167 27.9113C11.0994 27.9265 12.783 27.2547 14.04 26.0407L16.32 23.7607'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ArrowLeftIcon: FC = () => (
  <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
    <path
      d='M13.3337 8H2.66699M2.66699 8L8.00033 13.3333M2.66699 8L8.00033 2.66667'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

interface ConnectedAccountsSectionProps {
  /**
   * Notifies the parent when the section switches between the hub list and a
   * per-app detail view, so mobile settings can hide its own Back pill while
   * the detail view shows its own.
   */
  onViewChange?: (view: ConnectedAppsView) => void;
}

const ConnectedAccountsSection: FC<ConnectedAccountsSectionProps> = ({
  onViewChange,
}) => {
  const { getAccessTokenSilently, user } = useAuth0();
  const { secretaryWhatsappMacEnabled, secretaryLinkedinEnabled } =
    useFeatureFlags();
  const { agents } = useAgent();
  const assistantName =
    agents.find((a) => (a.agent_type as string) === 'Personal Assistant')
      ?.nickname || 'your secretary';
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<ConnectedAppsView>('list');
  useEffect(() => {
    onViewChange?.(view);
  }, [view, onViewChange]);
  const [accounts, setAccounts] = useState<GoogleLinkedAccount[]>([]);
  const [microsoftAccounts, setMicrosoftAccounts] = useState<
    MicrosoftLinkedAccount[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const handleWhatsappMacConnected = useCallback(() => {
    setFeedback({ type: 'success', message: 'Your Mac is connected' });
    setTimeout(() => setFeedback(null), 3000);
  }, []);
  const [disconnectTarget, setDisconnectTarget] =
    useState<DisconnectTarget | null>(null);

  // WhatsApp state
  const { messagingState, refreshStatus } = useMessaging();
  const [whatsappNumber, setWhatsappNumber] = useState<string | null>(null);
  const [whatsappLoading, setWhatsappLoading] = useState(true);
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [regCodeLoading, setRegCodeLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState<any>(null);
  const [regCodeError, setRegCodeError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // Todoist state
  const [todoistAccounts, setTodoistAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [todoistLoading, setTodoistLoading] = useState(true);

  // Asana state
  const [asanaAccounts, setAsanaAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [asanaLoading, setAsanaLoading] = useState(true);

  // Xero state
  const [xeroAccounts, setXeroAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [xeroLoading, setXeroLoading] = useState(true);

  // Lovable state
  const [lovableAccounts, setLovableAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [lovableLoading, setLovableLoading] = useState(true);
  const [lovableConnectAvailable, setLovableConnectAvailable] = useState(false);
  const [lovableFeedback, setLovableFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Granola state
  const [granolaAccounts, setGranolaAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [granolaLoading, setGranolaLoading] = useState(true);
  const [granolaFeedback, setGranolaFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Lusha state
  const [lushaAccounts, setLushaAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [lushaLoading, setLushaLoading] = useState(true);
  const [lushaApiKey, setLushaApiKey] = useState('');
  const [lushaConnecting, setLushaConnecting] = useState(false);
  const [lushaFeedback, setLushaFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Devin state
  const [devinAccounts, setDevinAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [devinLoading, setDevinLoading] = useState(true);
  const [devinApiKey, setDevinApiKey] = useState('');
  const [devinConnecting, setDevinConnecting] = useState(false);
  const [devinFeedback, setDevinFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Slack state
  const [slackAccounts, setSlackAccounts] = useState<
    { id: number; workspace: string; scope: string }[]
  >([]);
  const [slackLoading, setSlackLoading] = useState(true);

  // Linear state
  const [linearAccounts, setLinearAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [linearLoading, setLinearLoading] = useState(true);
  const [mondayAccounts, setMondayAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [mondayInstallUrl, setMondayInstallUrl] = useState<string | null>(null);
  const [mondayLoading, setMondayLoading] = useState(true);

  // Notion state
  const [notionAccounts, setNotionAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [notionLoading, setNotionLoading] = useState(true);

  // Zoom state (connected via Composio managed auth on the backend)
  const [zoomAccounts, setZoomAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [zoomLoading, setZoomLoading] = useState(true);

  // Attio state
  const [attioAccounts, setAttioAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [attioLoading, setAttioLoading] = useState(true);

  // HubSpot state
  const [hubspotAccounts, setHubspotAccounts] = useState<
    { id: number; email: string; scope: string }[]
  >([]);
  const [hubspotLoading, setHubspotLoading] = useState(true);

  // Whether the access ledger has any rows at all (kept after apps are
  // disconnected), so the entry row stays reachable for users with history.
  const [hasActivityHistory, setHasActivityHistory] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetGoogleLinkedAccountsResponse>(
        '/google-link',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAccounts(res.data.linkedAccounts);
      const msRes = await api.get<{
        linkedAccounts: MicrosoftLinkedAccount[];
      }>('/microsoft-link', { headers: { Authorization: `Bearer ${token}` } });
      setMicrosoftAccounts(msRes.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching linked accounts:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchWhatsappNumber = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetAgentWhatsappNumberResponse>(
        '/agent-whatsapp/number',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setWhatsappNumber(res.data.whatsapp_number || null);
    } catch (err) {
      console.error('Error fetching WhatsApp number:', err);
    } finally {
      setWhatsappLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchTodoistAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/todoist-link', { headers: { Authorization: `Bearer ${token}` } });
      setTodoistAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Todoist accounts:', err);
    } finally {
      setTodoistLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchAsanaAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/asana-link', { headers: { Authorization: `Bearer ${token}` } });
      setAsanaAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Asana accounts:', err);
    } finally {
      setAsanaLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchXeroAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/xero-link', { headers: { Authorization: `Bearer ${token}` } });
      setXeroAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Xero accounts:', err);
    } finally {
      setXeroLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchLovableAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
        connectAvailable?: boolean;
      }>('/lovable-link', { headers: { Authorization: `Bearer ${token}` } });
      setLovableAccounts(res.data.linkedAccounts);
      setLovableConnectAvailable(res.data.connectAvailable === true);
    } catch (err) {
      console.error('Error fetching Lovable accounts:', err);
    } finally {
      setLovableLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchGranolaAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/granola-link', { headers: { Authorization: `Bearer ${token}` } });
      setGranolaAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Granola accounts:', err);
    } finally {
      setGranolaLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchLushaAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/lusha-link', { headers: { Authorization: `Bearer ${token}` } });
      setLushaAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Lusha accounts:', err);
    } finally {
      setLushaLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchDevinAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/devin-link', { headers: { Authorization: `Bearer ${token}` } });
      setDevinAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Devin accounts:', err);
    } finally {
      setDevinLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchSlackAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; workspace: string; scope: string }[];
      }>('/slack-user-link', { headers: { Authorization: `Bearer ${token}` } });
      setSlackAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Slack accounts:', err);
    } finally {
      setSlackLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchLinearAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/linear-link', { headers: { Authorization: `Bearer ${token}` } });
      setLinearAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Linear accounts:', err);
    } finally {
      setLinearLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchMondayAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetMondayLinkedAccountsResponse>(
        '/monday-link',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMondayAccounts(res.data.linkedAccounts);
      setMondayInstallUrl(res.data.installUrl ?? null);
    } catch (err) {
      console.error('Error fetching Monday.com accounts:', err);
    } finally {
      setMondayLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchNotionAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/notion-link', { headers: { Authorization: `Bearer ${token}` } });
      setNotionAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Notion accounts:', err);
    } finally {
      setNotionLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchZoomAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/composio-link', { headers: { Authorization: `Bearer ${token}` } });
      setZoomAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Zoom accounts:', err);
    } finally {
      setZoomLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchAttioAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/attio-link', { headers: { Authorization: `Bearer ${token}` } });
      setAttioAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching Attio accounts:', err);
    } finally {
      setAttioLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchHubspotAccounts = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{
        linkedAccounts: { id: number; email: string; scope: string }[];
      }>('/hubspot-link', { headers: { Authorization: `Bearer ${token}` } });
      setHubspotAccounts(res.data.linkedAccounts);
    } catch (err) {
      console.error('Error fetching HubSpot accounts:', err);
    } finally {
      setHubspotLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchActivityHistoryFlag = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetConnectedAppActivityResponse>(
        '/connected-app-activity',
        { params: { limit: 1 }, headers: { Authorization: `Bearer ${token}` } },
      );
      setHasActivityHistory(res.data.events.length > 0);
    } catch (err) {
      console.error('Error checking connected-app activity history:', err);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchAccounts();
    fetchWhatsappNumber();
    fetchTodoistAccounts();
    fetchAsanaAccounts();
    fetchXeroAccounts();
    fetchGranolaAccounts();
    fetchLovableAccounts();
    fetchSlackAccounts();
    fetchLinearAccounts();
    fetchMondayAccounts();
    fetchNotionAccounts();
    fetchZoomAccounts();
    fetchAttioAccounts();
    fetchHubspotAccounts();
    fetchLushaAccounts();
    fetchDevinAccounts();
    fetchActivityHistoryFlag();
  }, [
    fetchAccounts,
    fetchWhatsappNumber,
    fetchTodoistAccounts,
    fetchAsanaAccounts,
    fetchXeroAccounts,
    fetchGranolaAccounts,
    fetchLovableAccounts,
    fetchSlackAccounts,
    fetchLinearAccounts,
    fetchMondayAccounts,
    fetchNotionAccounts,
    fetchZoomAccounts,
    fetchAttioAccounts,
    fetchHubspotAccounts,
    fetchLushaAccounts,
    fetchDevinAccounts,
    fetchActivityHistoryFlag,
  ]);

  // Refetch WhatsApp number when messaging state changes
  useEffect(() => {
    if (messagingState.whatsapp.loaded) {
      fetchWhatsappNumber();
    }
  }, [messagingState.whatsapp.connected]);

  // Close modal when WhatsApp becomes connected
  useEffect(() => {
    if (messagingState.whatsapp.connected && showWhatsappModal) {
      setShowWhatsappModal(false);
      setFeedback({
        type: 'success',
        message: 'WhatsApp connected successfully',
      });
      setTimeout(() => setFeedback(null), 3000);
    }
  }, [messagingState.whatsapp.connected, showWhatsappModal]);

  // Handle return from OAuth callback
  useEffect(() => {
    const appView = viewForAppParam(searchParams.get('app'));
    if (appView) {
      setView(appView);
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('app');
      nextSearchParams.delete('action');
      setSearchParams(nextSearchParams, { replace: true });
    }

    const status = searchParams.get('status');
    if (status === 'success') {
      fetchAccounts();
      fetchTodoistAccounts();
      fetchAsanaAccounts();
      fetchXeroAccounts();
      fetchGranolaAccounts();
      fetchLovableAccounts();
      fetchLinearAccounts();
      fetchMondayAccounts();
      fetchNotionAccounts();
      fetchZoomAccounts();
      fetchAttioAccounts();
      fetchHubspotAccounts();
      fetchSlackAccounts();
      const noMailbox = searchParams.get('noMailbox') === '1';
      const missing = (searchParams.get('missing') || '')
        .split(',')
        .filter(Boolean)
        .filter((label) => !(noMailbox && label === 'Email'));
      const notes: string[] = [];
      if (noMailbox) {
        notes.push(NO_MAILBOX_MESSAGE);
      }
      if (missing.length > 0) {
        notes.push(
          `${missing.join(', ')} access was not granted, so your secretary won\u2019t be able to help with ${missing.length > 1 ? 'those areas' : 'that area'} on this account. You can grant access at any time by reconnecting the account.`,
        );
      }
      setFeedback({
        type: 'success',
        message:
          notes.length > 0
            ? `Account connected. ${notes.join(' ')}`
            : 'Account connected successfully',
      });
      setTimeout(() => setFeedback(null), notes.length > 0 ? 12000 : 3000);
    } else if (status === 'error') {
      setFeedback({
        type: 'error',
        message: 'Failed to connect account. Please try again.',
      });
    }
  }, []);

  const openView = useCallback((next: ConnectedAppsView) => {
    setFeedback(null);
    setView(next);
  }, []);

  const backToList = useCallback(() => {
    setFeedback(null);
    setView('list');
  }, []);

  const handleConnectAccount = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        scope: 'email calendar workspace',
        page_id: 'settings',
      });
      const { data } = await api.get<{ url: string }>(
        `/google-link/initiate-google-oauth?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = data.url;
    } catch (err) {
      console.error('Error starting Google connect flow:', err);
    }
  }, [getAccessTokenSilently]);

  const startMicrosoftOauth = useCallback(
    async (scope: string) => {
      try {
        const token = await getAccessTokenSilently();
        const params = new URLSearchParams({ scope, page_id: 'settings' });
        const { data } = await api.get<{ url: string }>(
          `/microsoft-link/initiate-microsoft-oauth?${params}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        window.location.href = data.url;
      } catch (err) {
        console.error('Error starting Microsoft connect flow:', err);
      }
    },
    [getAccessTokenSilently],
  );

  const handleConnectMicrosoftAccount = useCallback(
    () => startMicrosoftOauth('email calendar'),
    [startMicrosoftOauth],
  );

  // Re-consent on an existing work account to add OnlineMeetings.ReadWrite so
  // Dream can mint Teams meeting links for events on non-Outlook calendars.
  const handleEnableTeamsMeetingLinks = useCallback(
    () => startMicrosoftOauth('email calendar teams'),
    [startMicrosoftOauth],
  );

  const handleRemoveMicrosoftAccount = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/microsoft-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setMicrosoftAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Account disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect account.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleRemoveAccount = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/google-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Account disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err: any) {
        const errorData = err?.response?.data;
        if (errorData?.error === 'Account in use') {
          setFeedback({
            type: 'error',
            message: `This account is in use by ${errorData.profiles.length} profile(s) and cannot be removed.`,
          });
        } else {
          console.error('Error removing account:', err);
          setFeedback({
            type: 'error',
            message: 'Failed to disconnect account.',
          });
        }
      }
    },
    [getAccessTokenSilently],
  );

  const handleConnectWhatsapp = useCallback(async () => {
    setShowWhatsappModal(true);
    setRegCodeLoading(true);
    setRegCodeError(null);
    setRegistrationData(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/agent-whatsapp/registration-code',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRegistrationData(response.data);
    } catch (err) {
      console.error('Error getting WhatsApp registration code:', err);
      setRegCodeError(
        'Failed to generate registration code. Please try again.',
      );
    } finally {
      setRegCodeLoading(false);
    }
  }, [getAccessTokenSilently]);

  const handleConnectTodoist = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/todoist-link/initiate-todoist-oauth?${params}`;
  }, [user]);

  const handleConnectAsana = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/asana-link/initiate-asana-oauth?${params}`;
  }, [user]);

  const handleConnectMonday = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/monday-link/initiate-monday-oauth?${params}`;
  }, [user]);

  const handleConnectXero = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/xero-link/initiate-xero-oauth?${params}`;
  }, [user]);

  // Deep link the assistant hands out when an expense cannot be filed because
  // no accounting app is connected: /settings/connected-accounts?app=xero&action=connect
  // starts the Xero OAuth flow as soon as the signed-in user is known (the
  // query params are stripped on mount, so the intent is captured up front).
  const { connection: ocado, refresh: refreshOcado } = useOcadoConnection();
  const {
    connection: linkedinConnection,
    loading: linkedinLoading,
    refresh: refreshLinkedinConnection,
  } = useLinkedinConnection(secretaryLinkedinEnabled);
  const {
    devices: whatsappMacDevices,
    refreshDevices: refreshWhatsappMacDevices,
  } = useWhatsappMacDevices(secretaryWhatsappMacEnabled);
  // /settings/connected-accounts?app=ocado&action=connect (the assistant's
  // sign-in link) opens the Ocado log-in straight away.
  const ocadoConnectRequested = useRef(
    searchParams.get('app') === 'ocado' &&
      searchParams.get('action') === 'connect',
  );

  const xeroConnectRequested = useRef(
    searchParams.get('app') === 'xero' &&
      searchParams.get('action') === 'connect',
  );
  useEffect(() => {
    if (!xeroConnectRequested.current || !user?.sub) return;
    xeroConnectRequested.current = false;
    handleConnectXero();
  }, [user?.sub, handleConnectXero]);

  const handleRemoveXero = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/xero-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setXeroAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Xero disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Xero account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Xero.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleRemoveTodoist = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/todoist-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setTodoistAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Todoist disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Todoist account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Todoist.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleRemoveAsana = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/asana-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setAsanaAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Asana disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Asana account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Asana.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleRemoveMonday = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/monday-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setMondayAccounts((prev) =>
          prev.filter((account) => account.id !== oauthId),
        );
        setFeedback({ type: 'success', message: 'Monday.com disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Monday.com account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Monday.com.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleConnectLovable = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/lovable-link/initiate-lovable-oauth?${params}`;
  }, [user]);

  const handleConnectGranola = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/granola-link/initiate-granola-oauth?${params}`;
  }, [user]);

  const handleConnectLusha = useCallback(async () => {
    if (!lushaApiKey.trim()) return;
    setLushaConnecting(true);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/lusha-link/connect',
        { apiKey: lushaApiKey.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLushaApiKey('');
      await fetchLushaAccounts();
      setLushaFeedback({ type: 'success', message: 'Lusha connected' });
      setTimeout(() => setLushaFeedback(null), 3000);
    } catch (err: any) {
      console.error('Error connecting Lusha account:', err);
      setLushaFeedback({
        type: 'error',
        message:
          err?.response?.data?.error ||
          'Failed to connect Lusha. Check your API key.',
      });
    } finally {
      setLushaConnecting(false);
    }
  }, [lushaApiKey, getAccessTokenSilently, fetchLushaAccounts]);

  const handleConnectDevin = useCallback(async () => {
    if (!devinApiKey.trim()) return;
    setDevinConnecting(true);
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/devin-link/connect',
        { apiKey: devinApiKey.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDevinApiKey('');
      await fetchDevinAccounts();
      setDevinFeedback({ type: 'success', message: 'Devin connected' });
      setTimeout(() => setDevinFeedback(null), 3000);
    } catch (err: any) {
      console.error('Error connecting Devin account:', err);
      setDevinFeedback({
        type: 'error',
        message:
          err?.response?.data?.error ||
          'Failed to connect Devin. Check your service-user key.',
      });
    } finally {
      setDevinConnecting(false);
    }
  }, [devinApiKey, getAccessTokenSilently, fetchDevinAccounts]);

  const handleRemoveDevin = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/devin-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setDevinAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setDevinFeedback({
          type: 'success',
          message: 'Devin disconnected',
        });
        setTimeout(() => setDevinFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Devin account:', err);
        setDevinFeedback({
          type: 'error',
          message: 'Failed to disconnect Devin.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleConnectSlack = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/slack-user-link/initiate?${params}`;
  }, [user]);

  const handleRemoveSlack = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/slack-user-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setSlackAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Slack disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Slack account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Slack.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleConnectLinear = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/linear-link/initiate-linear-oauth?${params}`;
  }, [user]);

  const handleRemoveLinear = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/linear-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setLinearAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Linear disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Linear account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Linear.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleConnectNotion = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({ page_id: 'settings' });
      const { data } = await api.get<{ url: string }>(
        `/notion-link/initiate-notion-oauth?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = data.url;
    } catch (err) {
      console.error('Error starting Notion connect flow:', err);
      setFeedback({
        type: 'error',
        message: 'Could not start the Notion connection. Please try again.',
      });
      setTimeout(() => setFeedback(null), 5000);
    }
  }, [getAccessTokenSilently]);

  // Deep link the assistant hands out when it needs more Notion pages shared:
  // /settings/connected-accounts?app=notion&action=connect goes straight to
  // Notion's page picker instead of making the user find the Connect button.
  useEffect(() => {
    if (
      searchParams.get('app') === 'notion' &&
      searchParams.get('action') === 'connect'
    ) {
      handleConnectNotion();
    }
  }, []);

  const handleRemoveNotion = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/notion-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setNotionAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Notion disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Notion account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Notion.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleConnectZoom = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/composio-link/initiate-composio-oauth?${params}`;
  }, [user]);

  const handleRemoveZoom = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/composio-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setZoomAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Zoom disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Zoom account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Zoom.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleConnectAttio = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/attio-link/initiate-attio-oauth?${params}`;
  }, [user]);

  const handleRemoveAttio = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/attio-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setAttioAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'Attio disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Attio account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect Attio.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleConnectHubspot = useCallback(() => {
    if (!user?.sub) return;
    const params = new URLSearchParams({
      user_id: user.sub,
      page_id: 'settings',
    });
    window.location.href = `${process.env.REACT_APP_API_URL}/hubspot-link/initiate-hubspot-oauth?${params}`;
  }, [user]);

  const handleRemoveHubspot = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/hubspot-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setHubspotAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setFeedback({ type: 'success', message: 'HubSpot disconnected' });
        setTimeout(() => setFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing HubSpot account:', err);
        setFeedback({
          type: 'error',
          message: 'Failed to disconnect HubSpot.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleRemoveLovable = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/lovable-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setLovableAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setLovableFeedback({
          type: 'success',
          message: 'Lovable disconnected',
        });
        setTimeout(() => setLovableFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Lovable account:', err);
        setLovableFeedback({
          type: 'error',
          message: 'Failed to disconnect Lovable.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleRemoveGranola = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/granola-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setGranolaAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setGranolaFeedback({
          type: 'success',
          message: 'Granola disconnected',
        });
        setTimeout(() => setGranolaFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Granola account:', err);
        setGranolaFeedback({
          type: 'error',
          message: 'Failed to disconnect Granola.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleRemoveLusha = useCallback(
    async (oauthId: number, deleteLearnedData = false) => {
      try {
        const token = await getAccessTokenSilently();
        await api.delete(
          disconnectPath('/lusha-link', oauthId, deleteLearnedData),
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        setLushaAccounts((prev) => prev.filter((a) => a.id !== oauthId));
        setLushaFeedback({
          type: 'success',
          message: 'Lusha disconnected',
        });
        setTimeout(() => setLushaFeedback(null), 3000);
      } catch (err) {
        console.error('Error removing Lusha account:', err);
        setLushaFeedback({
          type: 'error',
          message: 'Failed to disconnect Lusha.',
        });
      }
    },
    [getAccessTokenSilently],
  );

  const handleDisconnectWhatsapp = useCallback(async () => {
    setDisconnecting(true);
    try {
      const token = await getAccessTokenSilently();
      await api.delete('/agent-whatsapp/connection', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWhatsappNumber(null);
      await refreshStatus();
      setFeedback({ type: 'success', message: 'WhatsApp disconnected' });
      setTimeout(() => setFeedback(null), 3000);
    } catch (err) {
      console.error('Error disconnecting WhatsApp:', err);
      setFeedback({ type: 'error', message: 'Failed to disconnect WhatsApp.' });
    } finally {
      setDisconnecting(false);
    }
  }, [getAccessTokenSilently, refreshStatus]);

  // ── Hub list rows ─────────────────────────────────────────────────────

  const anyLoading =
    loading ||
    whatsappLoading ||
    todoistLoading ||
    asanaLoading ||
    xeroLoading ||
    granolaLoading ||
    lovableLoading ||
    slackLoading ||
    linearLoading ||
    mondayLoading ||
    notionLoading ||
    zoomLoading ||
    attioLoading ||
    hubspotLoading ||
    lushaLoading ||
    devinLoading;

  const whatsappChatConnected =
    messagingState.whatsapp.connected && !!whatsappNumber;

  type HubRow = {
    key: ConnectedAppsView | 'whatsapp_mac';
    icon: React.ReactNode;
    label: React.ReactNode;
    connected: boolean;
    accountCount?: number;
    /** Shown instead of "Connected" (e.g. Ocado logged out). */
    status?: string;
  };

  const hubRows: HubRow[] = useMemo(
    () => [
      {
        key: 'email',
        icon: <GoogleLogo />,
        label: (
          <>
            <strong>Google</strong> Email, Calendar &amp; Drive
          </>
        ),
        connected: accounts.length > 0,
        accountCount: accounts.length,
      },
      {
        key: 'outlook',
        icon: <img src={outlookLogo} alt='Outlook' />,
        label: (
          <>
            <strong>Outlook</strong> Email, Calendar &amp; Teams
          </>
        ),
        connected: microsoftAccounts.length > 0,
        accountCount: microsoftAccounts.length,
      },
      {
        key: 'whatsapp',
        icon: <WhatsappIcon className='whatsapp-icon' />,
        label: (
          <>
            Chat to {assistantName} on <strong>WhatsApp</strong>
          </>
        ),
        connected: whatsappChatConnected,
      },
      ...(secretaryLinkedinEnabled
        ? [
            {
              key: 'linkedin' as const,
              icon: <LinkedinIcon />,
              label: (
                <>
                  Let {assistantName} check your <strong>LinkedIn</strong> inbox
                </>
              ),
              connected: linkedinConnection.state === 'connected',
              status:
                linkedinConnection.state === 'logged_out'
                  ? 'Signed out'
                  : undefined,
            },
          ]
        : []),
      ...(secretaryWhatsappMacEnabled
        ? [
            {
              key: 'whatsapp_mac' as const,
              icon: <WhatsappIcon className='whatsapp-icon' />,
              label: (
                <>
                  Let {assistantName} check your <strong>WhatsApp</strong>
                </>
              ),
              connected: whatsappMacDevices.length > 0,
            },
          ]
        : []),
      {
        key: 'todoist',
        icon: <TodoistLogo />,
        label: (
          <>
            Sync your reminders to <strong>Todoist</strong>
          </>
        ),
        connected: todoistAccounts.length > 0,
        accountCount: todoistAccounts.length,
      },
      {
        key: 'asana',
        icon: <AsanaLogo />,
        label: (
          <>
            Sync your reminders to <strong>Asana</strong>
          </>
        ),
        connected: asanaAccounts.length > 0,
        accountCount: asanaAccounts.length,
      },
      {
        key: 'xero',
        icon: <img src={xeroLogo} alt='Xero' />,
        label: (
          <>
            Track expenses and reports with <strong>Xero</strong>
          </>
        ),
        connected: xeroAccounts.length > 0,
        accountCount: xeroAccounts.length,
      },
      {
        key: 'granola',
        icon: <img src={granolaLogo} alt='Granola' />,
        label: (
          <>
            Share notes and transcripts from <strong>Granola</strong>
          </>
        ),
        connected: granolaAccounts.length > 0,
        accountCount: granolaAccounts.length,
      },
      {
        key: 'lovable',
        icon: <img src={lovableLogo} alt='Lovable' />,
        label: (
          <>
            Build and update websites with <strong>Lovable</strong>
          </>
        ),
        connected: lovableAccounts.length > 0,
        accountCount: lovableAccounts.length,
      },
      {
        key: 'slack',
        icon: <SlackLogo />,
        label: (
          <>
            Read messages and search your <strong>Slack</strong> workspace
          </>
        ),
        connected: slackAccounts.length > 0,
        accountCount: slackAccounts.length,
      },
      {
        key: 'linear',
        icon: <LinearLogo />,
        label: (
          <>
            Manage issues and projects in <strong>Linear</strong>
          </>
        ),
        connected: linearAccounts.length > 0,
        accountCount: linearAccounts.length,
      },
      {
        key: 'monday',
        icon: <MondayLogo />,
        label: (
          <>
            Sync tasks and boards from <strong>Monday.com</strong>
          </>
        ),
        connected: mondayAccounts.length > 0,
        accountCount: mondayAccounts.length,
      },
      {
        key: 'notion',
        icon: <NotionLogo />,
        label: (
          <>
            Read and update pages, lists and databases in{' '}
            <strong>Notion</strong>
          </>
        ),
        connected: notionAccounts.length > 0,
        accountCount: notionAccounts.length,
      },
      {
        key: 'zoom',
        icon: <ZoomLogo />,
        label: (
          <>
            Add <strong>Zoom</strong> links to your meetings
          </>
        ),
        connected: zoomAccounts.length > 0,
        accountCount: zoomAccounts.length,
      },
      {
        key: 'attio',
        icon: <AttioLogo />,
        label: (
          <>
            Find people and log notes in <strong>Attio</strong>
          </>
        ),
        connected: attioAccounts.length > 0,
        accountCount: attioAccounts.length,
      },
      {
        key: 'hubspot',
        icon: <HubspotLogo />,
        label: (
          <>
            Find contacts and log notes in <strong>HubSpot</strong>
          </>
        ),
        connected: hubspotAccounts.length > 0,
        accountCount: hubspotAccounts.length,
      },
      {
        key: 'lusha',
        icon: <LushaLogo />,
        label: (
          <>
            Find new customers with <strong>Lusha</strong>
          </>
        ),
        connected: lushaAccounts.length > 0,
        accountCount: lushaAccounts.length,
      },
      {
        key: 'devin',
        icon: <DevinLogo />,
        label: (
          <>
            Monitor and start coding sessions with <strong>Devin</strong>
          </>
        ),
        connected: devinAccounts.length > 0,
        accountCount: devinAccounts.length,
      },
      ...(ocado
        ? [
            {
              key: 'ocado' as const,
              icon: <OcadoLogo />,
              label: (
                <>
                  Do your weekly shop with <strong>Ocado</strong>
                </>
              ),
              connected: ocado.state !== 'none',
              status: ocado.state === 'logged_out' ? 'Logged out' : undefined,
            },
          ]
        : []),
    ],
    [
      accounts.length,
      microsoftAccounts.length,
      assistantName,
      whatsappChatConnected,
      secretaryWhatsappMacEnabled,
      secretaryLinkedinEnabled,
      linkedinConnection,
      whatsappMacDevices.length,
      todoistAccounts.length,
      asanaAccounts.length,
      xeroAccounts.length,
      granolaAccounts.length,
      lovableAccounts.length,
      slackAccounts.length,
      linearAccounts.length,
      mondayAccounts.length,
      notionAccounts.length,
      zoomAccounts.length,
      attioAccounts.length,
      hubspotAccounts.length,
      lushaAccounts.length,
      devinAccounts.length,
      ocado,
    ],
  );

  const connectedRows = hubRows.filter((r) => r.connected);
  // Lovable is only offered to users the backend has released it to; an
  // existing connection still shows so it can be managed.
  const addMoreRows = hubRows.filter(
    (r) => !r.connected && (r.key !== 'lovable' || lovableConnectAvailable),
  );

  // Ledger provider code → logo, so the activity page matches the hub list.
  const activityProviderIcons: Record<string, React.ReactNode> = useMemo(
    () => ({
      GOOGLE: <GoogleLogo />,
      GOOGLE_WORKSPACE: <GoogleLogo />,
      MICROSOFT: <img src={outlookLogo} alt='Outlook' />,
      WHATSAPP: <WhatsappIcon className='whatsapp-icon' />,
      TODOIST: <TodoistLogo />,
      ASANA: <AsanaLogo />,
      XERO: <img src={xeroLogo} alt='Xero' />,
      GRANOLA: <img src={granolaLogo} alt='Granola' />,
      LOVABLE: <img src={lovableLogo} alt='Lovable' />,
      SLACK_USER: <SlackLogo />,
      LINEAR: <LinearLogo />,
      MONDAY: <MondayLogo />,
      NOTION: <NotionLogo />,
      ZOOM: <ZoomLogo />,
      ATTIO: <AttioLogo />,
      HUBSPOT: <HubspotLogo />,
      LUSHA: <LushaLogo />,
      DEVIN: <DevinLogo />,
    }),
    [],
  );

  // ── Renderers ─────────────────────────────────────────────────────────

  const renderFeedback = () =>
    feedback && (
      <p className={`connected-accounts-feedback ${feedback.type}`}>
        {feedback.message}
      </p>
    );

  const renderDetailHeader = (title: string) => (
    <div className='ca-detail-header'>
      <button className='ca-back-btn' onClick={backToList}>
        <ArrowLeftIcon />
        <span>Back</span>
      </button>
      <h2 className='ca-detail-title'>{title}</h2>
    </div>
  );

  const renderAccountRow = (
    oauthId: number,
    icon: React.ReactNode,
    label: string,
    scope: string,
    onRemove: (deleteLearnedData: boolean) => Promise<void>,
    providerLabel: string,
    missingCapabilities?: string[],
    capabilities?: React.ReactNode,
    mailboxUnavailable?: boolean,
  ) => {
    const ungranted = (missingCapabilities ?? []).filter(
      (capability) => !(mailboxUnavailable && capability === 'Email'),
    );
    return (
      <div key={oauthId} className='ca-account-row'>
        <div className='ca-account-info'>
          <span className='ca-account-icon'>{icon}</span>
          <span className='ca-account-label'>{label}</span>
          <span className='ca-account-scope'>{scope}</span>
          {mailboxUnavailable && (
            <span className='ca-account-limited' title={NO_MAILBOX_MESSAGE}>
              No Gmail inbox
            </span>
          )}
          {ungranted.length > 0 && (
            <span
              className='ca-account-limited'
              title={`${ungranted.join(', ')} access not granted on this account. Reconnect the account to grant it.`}
            >
              Limited access
            </span>
          )}
          {capabilities}
        </div>
        <button
          className='ca-remove-btn'
          onClick={() =>
            setDisconnectTarget({
              oauthId,
              label,
              providerLabel,
              run: onRemove,
            })
          }
          aria-label={`Disconnect ${label}`}
        >
          <CircleMinusIcon />
        </button>
      </div>
    );
  };

  const renderList = () => (
    <>
      <div className='ca-list-title-row'>
        <h2 className='ca-list-title'>Connected Apps</h2>
        {!anyLoading && connectedRows.length > 0 && (
          <span className='ca-list-count'>{connectedRows.length}</span>
        )}
        <p className='ca-list-subtitle'>
          Connect apps and see how your secretary uses them.
        </p>
      </div>

      {renderFeedback()}

      {anyLoading ? (
        <div className='settings-skeleton ca-list-skeleton' />
      ) : (
        <>
          <div className='ca-app-list'>
            {connectedRows.map((row, i) => (
              <button
                key={`connected-${i}`}
                className='ca-app-row'
                onClick={() =>
                  openView(row.key === 'whatsapp_mac' ? 'whatsapp' : row.key)
                }
              >
                <div className='ca-app-row-left'>
                  <span className='ca-app-icon'>{row.icon}</span>
                  <span className='ca-app-label'>{row.label}</span>
                </div>
                <div className='ca-app-row-right'>
                  {row.accountCount != null && row.accountCount > 0 && (
                    <>
                      <span className='ca-app-count'>
                        {row.accountCount} account
                        {row.accountCount !== 1 ? 's' : ''}
                      </span>
                      <span className='ca-app-dot'>•</span>
                    </>
                  )}
                  {row.status ? (
                    <span className='ca-account-limited'>{row.status}</span>
                  ) : (
                    <span className='ca-app-status'>Connected</span>
                  )}
                  <span className='ca-app-chevron'>
                    <ChevronRightIcon />
                  </span>
                </div>
              </button>
            ))}
            {connectedRows.length === 0 && (
              <div className='ca-empty' data-testid='ca-empty'>
                <span className='ca-empty-icon'>
                  <LinkIcon />
                </span>
                <p className='ca-empty-title'>
                  There&rsquo;s nothing connected
                </p>
                <p className='ca-empty-hint'>
                  Set up an app below so {assistantName} can help with more.
                </p>
              </div>
            )}
          </div>
          {(connectedRows.length > 0 || hasActivityHistory) && (
            <button
              className='ca-activity-row'
              onClick={() => openView('activity')}
              data-testid='ca-activity-row'
            >
              <div className='ca-app-row-left'>
                <span className='ca-activity-icon'>
                  <ActivityIcon />
                </span>
                <span className='ca-activity-label'>
                  See how {assistantName}{' '}
                  {connectedRows.length > 0 ? 'is using' : 'has used'} your apps
                </span>
              </div>
              <div className='ca-app-row-right'>
                <span className='ca-activity-open'>Open</span>
                <span className='ca-app-chevron ca-activity-chevron'>
                  <ChevronRightIcon />
                </span>
              </div>
            </button>
          )}

          {addMoreRows.length > 0 && (
            <>
              <div className='ca-list-title-row ca-add-more-title'>
                <h2 className='ca-list-title'>Add more</h2>
              </div>
              <div className='ca-app-list'>
                {addMoreRows.map((row, i) => (
                  <button
                    key={`add-${i}`}
                    className='ca-app-row'
                    onClick={() =>
                      openView(
                        row.key === 'whatsapp_mac' ? 'whatsapp' : row.key,
                      )
                    }
                  >
                    <div className='ca-app-row-left'>
                      <span className='ca-app-icon'>{row.icon}</span>
                      <span className='ca-app-label'>{row.label}</span>
                    </div>
                    <div className='ca-app-row-right'>
                      {row.key === 'monday' && (
                        <span className='ca-app-new'>New</span>
                      )}
                      <span className='ca-app-setup'>Setup</span>
                      <span className='ca-app-arrow'>
                        <ArrowRightIcon />
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}

          <p className='ca-more-apps-hint'>
            Want more? Connect TravelPerk, Navan and thousands of other apps by
            giving {assistantName} an account.
          </p>
        </>
      )}
    </>
  );

  const renderEmailView = () => (
    <>
      {renderDetailHeader('Connect Google Email, Calendar & Drive')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {loading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            <div className='ca-connect-row'>
              <button className='ca-connect-btn' onClick={handleConnectAccount}>
                <GoogleLogo />
                <span>Connect a Google account</span>
              </button>
              <p className='ca-connect-hint'>
                We support Gmail and Google Workspace accounts
              </p>
            </div>
            {accounts.length > 0 && (
              <div className='ca-account-list'>
                {accounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <GoogleLogo />,
                    account.email,
                    account.scope || 'Email, Calendar & Drive',
                    (deleteLearnedData) =>
                      handleRemoveAccount(account.id, deleteLearnedData),
                    'Google',
                    account.missingCapabilities,
                    undefined,
                    account.mailboxUnavailable,
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  // Teams *meeting links* only (not Teams chat) are part of the Outlook
  // account's scope line: "Email, Calendar & Teams" once the work account has
  // granted OnlineMeetings.ReadWrite. Outlook-hosted events always get their
  // link inline; the extra consent is for events on other calendars.
  const microsoftScopeLabel = (account: MicrosoftLinkedAccount) => {
    const base = account.scope || 'Email & Calendar';
    if (account.teamsMeetingLinks !== 'enabled') return base;
    return `${base.replace(' & ', ', ')} & Teams`;
  };

  const renderAddTeams = (account: MicrosoftLinkedAccount) =>
    account.teamsMeetingLinks === 'available' ? (
      <button
        type='button'
        className='ca-account-capability-btn'
        data-testid='teams-meeting-links-enable'
        title='Grant permission to create Microsoft Teams meeting links for events on your other calendars. Events on this Outlook calendar already get Teams links.'
        onClick={handleEnableTeamsMeetingLinks}
      >
        Add Teams
      </button>
    ) : null;

  const renderOutlookView = () => (
    <>
      {renderDetailHeader('Connect Outlook Email, Calendar & Teams')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {loading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            <div className='ca-connect-row'>
              <button
                className='ca-connect-btn'
                onClick={handleConnectMicrosoftAccount}
              >
                <img src={microsoftLogo} alt='Microsoft' />
                <span>Connect a Microsoft account</span>
              </button>
              <p className='ca-connect-hint'>
                We support Outlook.com and Microsoft 365 accounts
              </p>
            </div>
            {microsoftAccounts.length > 0 && (
              <div className='ca-account-list'>
                {microsoftAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <img src={outlookLogo} alt='Outlook' />,
                    account.email,
                    microsoftScopeLabel(account),
                    (deleteLearnedData) =>
                      handleRemoveMicrosoftAccount(
                        account.id,
                        deleteLearnedData,
                      ),
                    'Microsoft',
                    account.missingCapabilities,
                    renderAddTeams(account),
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderWhatsappView = () => (
    <>
      {renderDetailHeader('WhatsApp')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        <div className='ca-detail-block'>
          <h3 className='ca-block-title'>
            Chat to {assistantName} on WhatsApp
          </h3>
          <p className='ca-block-description'>
            Chat and receive messages from your secretary on WhatsApp
          </p>
          {whatsappLoading ? (
            <div className='settings-skeleton' />
          ) : whatsappChatConnected ? (
            <div className='ca-account-row'>
              <div className='ca-account-info'>
                <span className='ca-account-icon'>
                  <WhatsappIcon className='whatsapp-icon' />
                </span>
                <span className='ca-account-label'>{whatsappNumber}</span>
                <span className='ca-account-scope'>WhatsApp</span>
              </div>
              <button
                className='ca-remove-btn'
                onClick={handleDisconnectWhatsapp}
                disabled={disconnecting}
                aria-label='Disconnect WhatsApp'
              >
                <CircleMinusIcon />
              </button>
            </div>
          ) : (
            <button className='ca-connect-btn' onClick={handleConnectWhatsapp}>
              <WhatsappIcon className='whatsapp-icon' />
              <span>Chat on WhatsApp</span>
            </button>
          )}
        </div>
        {secretaryWhatsappMacEnabled && (
          <WhatsappMacSection
            assistantName={assistantName}
            devices={whatsappMacDevices}
            refreshDevices={refreshWhatsappMacDevices}
            onConnected={handleWhatsappMacConnected}
          />
        )}
      </div>
    </>
  );

  const renderTodoistView = () => (
    <>
      {renderDetailHeader('Todoist')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {todoistLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {todoistAccounts.length === 0 && (
              <div className='ca-connect-row'>
                <button
                  className='ca-connect-btn'
                  onClick={handleConnectTodoist}
                >
                  <TodoistLogo />
                  <span>Connect Todoist</span>
                </button>
                <p className='ca-connect-hint'>
                  Connect your Todoist account so {assistantName} can manage
                  reminders and tasks for you.
                </p>
              </div>
            )}
            {todoistAccounts.length > 0 && (
              <div className='ca-account-list'>
                {todoistAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <TodoistLogo />,
                    account.email,
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveTodoist(account.id, deleteLearnedData),

                    'Todoist',
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderAsanaView = () => (
    <>
      {renderDetailHeader('Asana')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {asanaLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {asanaAccounts.length === 0 && (
              <div className='ca-connect-row'>
                <button className='ca-connect-btn' onClick={handleConnectAsana}>
                  <AsanaLogo />
                  <span>Connect Asana</span>
                </button>
                <p className='ca-connect-hint'>
                  Connect your Asana account so {assistantName} can manage
                  reminders and tasks for you.
                </p>
              </div>
            )}
            {asanaAccounts.length > 0 && (
              <div className='ca-account-list'>
                {asanaAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <AsanaLogo />,
                    account.email,
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveAsana(account.id, deleteLearnedData),

                    'Asana',
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderXeroView = () => (
    <>
      {renderDetailHeader('Xero')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {xeroLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {xeroAccounts.length === 0 && (
              <div className='ca-connect-row'>
                <button className='ca-connect-btn' onClick={handleConnectXero}>
                  <img src={xeroLogo} alt='Xero' />
                  <span>Connect Xero</span>
                </button>
                <p className='ca-connect-hint'>
                  Connect your Xero account so {assistantName} can manage
                  transactions, receipts, and financial reports.
                </p>
              </div>
            )}
            {xeroAccounts.length > 0 && (
              <div className='ca-account-list'>
                {xeroAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <img src={xeroLogo} alt='Xero' />,
                    account.email,
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveXero(account.id, deleteLearnedData),

                    'Xero',
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderGranolaView = () => (
    <>
      {renderDetailHeader('Granola')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {granolaLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {granolaFeedback && (
              <p
                className={`connected-accounts-feedback ${granolaFeedback.type}`}
              >
                {granolaFeedback.message}
              </p>
            )}
            {granolaAccounts.length === 0 && (
              <div className='ca-connect-row'>
                <button
                  className='ca-connect-btn'
                  onClick={handleConnectGranola}
                >
                  <img src={granolaLogo} alt='Granola' />
                  <span>Connect my Granola</span>
                </button>
                <p className='ca-connect-hint'>
                  Sign in to Granola and allow access so {assistantName} can
                  read your meeting notes and call transcripts.
                </p>
              </div>
            )}
            {granolaAccounts.length > 0 && (
              <div className='ca-account-list'>
                {granolaAccounts.map((account) => {
                  const displayName =
                    account.email && account.email.includes('@')
                      ? account.email
                      : 'Granola account';
                  return renderAccountRow(
                    account.id,
                    <img src={granolaLogo} alt='Granola' />,
                    displayName,
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveGranola(account.id, deleteLearnedData),

                    'Granola',
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderLovableView = () => (
    <>
      {renderDetailHeader('Lovable')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {lovableLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {lovableFeedback && (
              <p
                className={`connected-accounts-feedback ${lovableFeedback.type}`}
              >
                {lovableFeedback.message}
              </p>
            )}
            {lovableAccounts.length === 0 && (
              <div className='ca-connect-row'>
                <button
                  className='ca-connect-btn'
                  onClick={handleConnectLovable}
                >
                  <img src={lovableLogo} alt='Lovable' />
                  <span>Connect my Lovable</span>
                </button>
                <p className='ca-connect-hint'>
                  Sign in to Lovable and allow access so {assistantName} can
                  build and update your websites. Publishing always waits for
                  your confirmation.
                </p>
              </div>
            )}
            {lovableAccounts.length > 0 && (
              <div className='ca-account-list'>
                {lovableAccounts.map((account) => {
                  const displayName =
                    account.email && account.email.includes('@')
                      ? account.email
                      : 'Lovable account';
                  return renderAccountRow(
                    account.id,
                    <img src={lovableLogo} alt='Lovable' />,
                    displayName,
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveLovable(account.id, deleteLearnedData),

                    'Lovable',
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderLushaView = () => (
    <>
      {renderDetailHeader('Lusha')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {lushaLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {lushaFeedback && (
              <p
                className={`connected-accounts-feedback ${lushaFeedback.type}`}
              >
                {lushaFeedback.message}
              </p>
            )}
            {lushaAccounts.length === 0 && (
              <>
                <div className='ca-granola-form'>
                  <input
                    type='password'
                    className='ca-granola-input'
                    placeholder='Paste your Lusha API key'
                    value={lushaApiKey}
                    onChange={(e) => setLushaApiKey(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleConnectLusha();
                    }}
                    autoComplete='off'
                  />
                  <button
                    className='ca-connect-btn'
                    onClick={handleConnectLusha}
                    disabled={lushaConnecting || !lushaApiKey.trim()}
                  >
                    <LushaLogo />
                    <span>
                      {lushaConnecting ? 'Connecting…' : 'Connect my Lusha'}
                    </span>
                  </button>
                </div>
                <p className='ca-connect-hint'>
                  Connect Lusha so {assistantName} can find new potential
                  customers — searching companies and decision-makers that match
                  your ideal customer profile, and revealing their contact
                  details when they are worth approaching. Create an API key in
                  Lusha under API &amp; Integrations → API access, then paste it
                  here. Revealing contact details spends your Lusha credits.
                </p>
              </>
            )}
            {lushaAccounts.length > 0 && (
              <div className='ca-account-list'>
                {lushaAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <LushaLogo />,
                    account.email || 'Lusha account',
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveLusha(account.id, deleteLearnedData),

                    'Lusha',
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderSlackView = () => (
    <>
      {renderDetailHeader('Slack')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {slackLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {slackAccounts.length === 0 && (
              <div className='ca-connect-row'>
                <button className='ca-connect-btn' onClick={handleConnectSlack}>
                  <SlackLogo />
                  <span>Connect Slack</span>
                </button>
                <p className='ca-connect-hint'>
                  Connect your Slack account so {assistantName} can read your
                  messages and search your workspace.
                </p>
              </div>
            )}
            {slackAccounts.length > 0 && (
              <div className='ca-account-list'>
                {slackAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <SlackLogo />,
                    account.workspace,
                    'Slack',
                    (deleteLearnedData) =>
                      handleRemoveSlack(account.id, deleteLearnedData),

                    'Slack',
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderNotionView = () => (
    <>
      {renderDetailHeader('Notion')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {notionLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {notionAccounts.length === 0 && (
              <div className='ca-connect-row'>
                <button
                  className='ca-connect-btn'
                  onClick={handleConnectNotion}
                >
                  <NotionLogo />
                  <span>Connect Notion</span>
                </button>
                <p className='ca-connect-hint'>
                  Connect your Notion workspace so {assistantName} can read and
                  update the pages, lists and databases you choose to share —
                  including shared to-do lists.
                </p>
              </div>
            )}
            {notionAccounts.length > 0 && (
              <div className='ca-account-list'>
                {notionAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <NotionLogo />,
                    account.email,
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveNotion(account.id, deleteLearnedData),

                    'Notion',
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderLinearView = () => (
    <>
      {renderDetailHeader('Linear')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {linearLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {linearAccounts.length === 0 && (
              <div className='ca-connect-row'>
                <button
                  className='ca-connect-btn'
                  onClick={handleConnectLinear}
                >
                  <LinearLogo />
                  <span>Connect Linear</span>
                </button>
                <p className='ca-connect-hint'>
                  Connect your Linear workspace so {assistantName} can read
                  through your issues and projects and create new ones for you.
                </p>
              </div>
            )}
            {linearAccounts.length > 0 && (
              <div className='ca-account-list'>
                {linearAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <LinearLogo />,
                    account.email,
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveLinear(account.id, deleteLearnedData),

                    'Linear',
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderZoomView = () => (
    <>
      {renderDetailHeader('Zoom')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {zoomLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {zoomAccounts.length === 0 && (
              <div className='ca-connect-row'>
                <button className='ca-connect-btn' onClick={handleConnectZoom}>
                  <ZoomLogo />
                  <span>Connect Zoom</span>
                </button>
                <p className='ca-connect-hint'>
                  Connect your Zoom account so {assistantName} can add unique
                  Zoom meeting links when scheduling your calls. You&apos;ll be
                  taken to Zoom to approve access via Composio, our secure
                  connection partner.
                </p>
              </div>
            )}
            {zoomAccounts.length > 0 && (
              <div className='ca-account-list'>
                {zoomAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <ZoomLogo />,
                    account.email,
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveZoom(account.id, deleteLearnedData),

                    'Zoom',
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderMondayView = () => (
    <IntegrationPanel
      provider={MONDAY_PROVIDER}
      connected={mondayAccounts.length > 0}
      onConnect={handleConnectMonday}
      onDisconnect={handleRemoveMonday}
      disconnectWarning={`${assistantName} will also forget what it learned about your boards and stop the jobs it's doing for you, including any routines you've switched on. Reconnecting Monday.com starts the analysis again from scratch.`}
      installationStep={
        mondayInstallUrl
          ? {
              heading: "Install Dream's Monday.com app",
              description:
                'An account admin needs to install the app before you connect your Monday.com account.',
              actionLabel: 'Install app',
              url: mondayInstallUrl,
            }
          : undefined
      }
      onBack={backToList}
      accounts={mondayAccounts}
      assistantName={assistantName}
    />
  );

  const renderAttioView = () => (
    <>
      {renderDetailHeader('Attio')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {attioLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {attioAccounts.length === 0 && (
              <div className='ca-connect-row'>
                <button className='ca-connect-btn' onClick={handleConnectAttio}>
                  <AttioLogo />
                  <span>Connect Attio</span>
                </button>
                <p className='ca-connect-hint'>
                  Connect your Attio workspace so {assistantName} can find
                  people in your CRM and log notes on their records for you.
                </p>
              </div>
            )}
            {attioAccounts.length > 0 && (
              <div className='ca-account-list'>
                {attioAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <AttioLogo />,
                    account.email,
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveAttio(account.id, deleteLearnedData),

                    'Attio',
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderHubspotView = () => (
    <>
      {renderDetailHeader('HubSpot')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {hubspotLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {hubspotAccounts.length === 0 && (
              <div className='ca-connect-row'>
                <button
                  className='ca-connect-btn'
                  onClick={handleConnectHubspot}
                >
                  <HubspotLogo />
                  <span>Connect HubSpot</span>
                </button>
                <p className='ca-connect-hint'>
                  Connect your HubSpot account so {assistantName} can find
                  contacts in your CRM and log notes on their records for you.
                </p>
              </div>
            )}
            {hubspotAccounts.length > 0 && (
              <div className='ca-account-list'>
                {hubspotAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <HubspotLogo />,
                    account.email,
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveHubspot(account.id, deleteLearnedData),

                    'HubSpot',
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderDevinView = () => (
    <>
      {renderDetailHeader('Devin')}
      {renderFeedback()}
      <div className='ca-detail-card'>
        {devinLoading ? (
          <div className='settings-skeleton' />
        ) : (
          <>
            {devinFeedback && (
              <p
                className={`connected-accounts-feedback ${devinFeedback.type}`}
              >
                {devinFeedback.message}
              </p>
            )}
            {devinAccounts.length === 0 && (
              <>
                <div className='ca-granola-form'>
                  <input
                    type='password'
                    className='ca-granola-input'
                    placeholder='Paste your Devin service-user key (cog_…)'
                    value={devinApiKey}
                    onChange={(e) => setDevinApiKey(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleConnectDevin();
                    }}
                    autoComplete='off'
                  />
                  <button
                    className='ca-connect-btn'
                    onClick={handleConnectDevin}
                    disabled={devinConnecting || !devinApiKey.trim()}
                  >
                    <DevinLogo />
                    <span>
                      {devinConnecting ? 'Connecting…' : 'Connect my Devin'}
                    </span>
                  </button>
                </div>
                <p className='ca-connect-hint'>
                  Connect Devin so {assistantName} can monitor its coding
                  sessions, start new ones, and send it follow-up instructions.
                  Create a service user in the Devin web app under Settings →
                  Service Users, then paste its key (cog_…) here.
                </p>
              </>
            )}
            {devinAccounts.length > 0 && (
              <div className='ca-account-list'>
                {devinAccounts.map((account) =>
                  renderAccountRow(
                    account.id,
                    <DevinLogo />,
                    account.email || 'Devin connection',
                    account.scope,
                    (deleteLearnedData) =>
                      handleRemoveDevin(account.id, deleteLearnedData),

                    'Devin',
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  return (
    <div className='connected-accounts-section'>
      {view === 'list' && renderList()}
      {view === 'email' && renderEmailView()}
      {view === 'outlook' && renderOutlookView()}
      {view === 'whatsapp' && renderWhatsappView()}
      {view === 'linkedin' && secretaryLinkedinEnabled && (
        <LinkedinConnectedApp
          connection={linkedinConnection}
          loading={linkedinLoading}
          assistantName={assistantName}
          header={renderDetailHeader('LinkedIn')}
          onChanged={refreshLinkedinConnection}
        />
      )}
      {view === 'todoist' && renderTodoistView()}
      {view === 'asana' && renderAsanaView()}
      {view === 'xero' && renderXeroView()}
      {view === 'granola' && renderGranolaView()}
      {view === 'lovable' && renderLovableView()}
      {view === 'ocado' && ocado && (
        <OcadoAppView
          connection={ocado}
          header={renderDetailHeader('Ocado')}
          assistantName={assistantName}
          autoConnect={ocadoConnectRequested.current}
          onChanged={refreshOcado}
        />
      )}
      {view === 'slack' && renderSlackView()}
      {view === 'linear' && renderLinearView()}
      {view === 'monday' && renderMondayView()}
      {view === 'notion' && renderNotionView()}
      {view === 'zoom' && renderZoomView()}
      {view === 'attio' && renderAttioView()}
      {view === 'hubspot' && renderHubspotView()}
      {view === 'lusha' && renderLushaView()}
      {view === 'devin' && renderDevinView()}
      {view === 'activity' && (
        <ConnectedAppActivityView
          onBack={backToList}
          assistantName={assistantName}
          providerIcons={activityProviderIcons}
        />
      )}

      <DisconnectAppModal
        target={disconnectTarget}
        onClose={() => setDisconnectTarget(null)}
        assistantName={assistantName}
      />

      {/* WhatsApp Registration Code Modal */}
      <Modal
        show={showWhatsappModal}
        onHide={() => setShowWhatsappModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Connect WhatsApp</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='whatsapp-reg-modal'>
            {regCodeLoading ? (
              <div className='whatsapp-reg-loading'>
                <Spinner animation='border' size='sm' />
                <p>Generating registration code...</p>
              </div>
            ) : regCodeError ? (
              <div className='whatsapp-reg-error'>{regCodeError}</div>
            ) : registrationData ? (
              <div className='whatsapp-reg-content'>
                <QRCodeSVG value={registrationData.whatsapp_url} size={200} />
                <p className='whatsapp-reg-code'>
                  Registration Code: <strong>{registrationData.code}</strong>
                </p>
                <p className='whatsapp-reg-instructions'>
                  Scan this QR code with your phone to open WhatsApp and send
                  the registration code. Or manually send the code to our
                  WhatsApp number.
                </p>
                <p className='whatsapp-reg-expiry'>Code expires in 24 hours.</p>
              </div>
            ) : null}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ConnectedAccountsSection;
